<template>
  <q-list v-if="notifications.length" separator no-border class="q-pa-none">
    <template v-for="(notif, index) in notifications">
      <q-collapsible
        v-if="notif.description"
        :key="notif.ID"
        :opened="index === 0 && isUnread(notif)"
        indent
        header-class="notif-row"
        :class="{ 'notif-row--unread': isUnread(notif) }"
        @show="onExpand(notif)"
      >
        <template slot="header">
          <div class="notif-row__icon" :class="`notif-row__icon--${iconColorFor(notif)}`">
            <q-icon :name="iconFor(notif)" size="18px" />
          </div>
          <div class="notif-row__body">
            <div class="notif-row__title">{{ notif.title }}</div>
            <div class="notif-row__snippet">{{ notif.snippet }}</div>
            <div v-if="displayDateFor(notif)" class="notif-row__meta">{{ displayDateFor(notif) | formatMonthYear }}</div>
          </div>
          <q-item-side v-if="isUnread(notif)" right>
            <span class="notif-row__dot" />
          </q-item-side>
        </template>

        <div v-html="notif.description" />

        <div v-if="notif.link || notif.action" class="row items-center q-gutter-sm q-mt-sm">
          <q-btn
            v-if="notif.link"
            flat
            dense
            no-caps
            color="primary"
            label="Mehr erfahren"
            icon-right="open_in_new"
            type="a"
            :href="notif.link"
            target="_blank"
          />
          <q-btn
            v-if="notif.action"
            flat
            dense
            no-caps
            color="primary"
            label="Öffnen"
            @click="notif.action()"
          />
        </div>
      </q-collapsible>

      <div v-else :key="notif.ID" :class="{ 'notif-row--unread': isUnread(notif) }">
        <div class="notif-row">
          <div class="notif-row__icon" :class="`notif-row__icon--${iconColorFor(notif)}`">
            <q-icon :name="iconFor(notif)" size="18px" />
          </div>
          <div class="notif-row__body">
            <div class="notif-row__title">{{ notif.title }}</div>
            <div class="notif-row__snippet">{{ notif.snippet }}</div>
            <div v-if="displayDateFor(notif)" class="notif-row__meta">{{ displayDateFor(notif) | formatMonthYear }}</div>
          </div>
          <q-item-side v-if="isUnread(notif)" right>
            <span class="notif-row__dot" />
          </q-item-side>
        </div>

        <div v-if="notif.link || notif.action" class="q-collapsible-sub-item indent row items-center q-gutter-sm">
          <q-btn
            v-if="notif.link"
            flat
            dense
            no-caps
            color="primary"
            label="Mehr erfahren"
            icon-right="open_in_new"
            type="a"
            :href="notif.link"
            target="_blank"
          />
          <q-btn
            v-if="notif.action"
            flat
            dense
            no-caps
            color="primary"
            label="Öffnen"
            @click="notif.action()"
          />
        </div>
      </div>
    </template>
  </q-list>

  <div v-else class="column items-center q-pa-lg text-faded">
    <q-icon name="notifications_none" size="28px" />
    <small>Keine Nachrichten</small>
  </div>
</template>

<script>
export default {
  name: 'NotificationList',

  props: {
    notifications: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      pendingRead: [],
    };
  },

  created() {
    this.notifications
      .filter(notif => !notif.description && this.isUnread(notif))
      .forEach(notif => this.onExpand(notif));
  },

  methods: {
    isUnread(notif) {
      return notif.onRead ? !!notif.unread : !notif.isRead;
    },

    onExpand(notif) {
      if (this.isUnread(notif) && !this.pendingRead.includes(notif)) {
        this.pendingRead.push(notif);
      }
    },

    flushPendingRead() {
      this.pendingRead.forEach(notif => this.markRead(notif));
      this.pendingRead = [];
    },

    iconFor(notif) {
      return notif.icon || (notif.type === 'update' ? 'update' : 'info');
    },

    displayDateFor(notif) {
      return notif.displayDate || notif.created;
    },

    iconColorFor(notif) {
      return this.isUnread(notif) ? 'primary' : 'grey';
    },

    markRead(notif) {
      if (notif.onRead) {
        if (notif.unread) notif.onRead();
        return;
      }

      if (!notif.isRead) {
        this.$store.dispatch('notifications/closeDialog', notif.ID);
      }
    },
  },
};
</script>
<style scoped lang="stylus">
@import theme

>>> .notif-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 20px;
}

>>> .notif-row .q-item-side-right:has(.q-collapsible-toggle-icon) {
  display: none;
}

>>> .q-collapsible-sub-item.indent {
  padding-left: 74px;
  padding-right: 20px;
}

.notif-row__icon {
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &--primary {
    background-color: rgba($primary, 0.12);
    color: $primary;
  }

  &--grey {
    background-color: rgba(0, 0, 0, 0.06);
    color: rgba(0, 0, 0, 0.5);
  }
}

.notif-row__body {
  flex: 1;
  min-width: 0;
}

.notif-row__title {
  font-weight: 600;
  font-size: 15px;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.9);
}

.notif-row__snippet {
  font-size: 14px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 2px;
}

.notif-row__meta {
  font-size: 12.5px;
  color: rgba(0, 0, 0, 0.4);
  margin-top: 2px;
}

.notif-row__dot {
  display: block;
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: $primary;
  margin-top: 6px;
}

>>> .notif-row--unread .notif-row,
>>> .notif-row--unread .q-collapsible-sub-item {
  background-color: rgba($primary, 0.08);
}
</style>
